import React, {ChangeEvent, KeyboardEvent, ReactElement} from "react";
import algoliasearch, {SearchIndex} from "algoliasearch";

import ModuleBody from "../../layouts/module-body/ModuleBody";
import Button from "../../buttons/button/Button";
import Image, {ImageObject} from "../../image/Image";
import CTAButton, {CTA} from "../../buttons/cta-button/CTAButton";
import Video, {VideoType} from "../../videos/video/Video";
import LinkTag from "../../buttons/link-tag/LinkTag";
import SearchPagination from "../../search/pagination/SearchPagination";

import translate from "/src/utils/translate";
import {css, generateKeyId} from "/src/utils/utils";
import {MaybeType} from "/src/types";
import KEY from "/src/constants/keyboard-keydown-key";

import * as styles from "./search-hero.module.scss";

interface SearchHeroProps {
  algoliaIndex?: MaybeType<string>;
  bgImage: MaybeType<ImageObject>;
  bgVideo: MaybeType<string>;
  wistiaBgVideo?: MaybeType<string>;
  copy: MaybeType<string>;
  ctas: MaybeType<Array<CTA>>;
  eyebrow: MaybeType<string>;
  eyebrowIcon: MaybeType<ImageObject>;
  headline: MaybeType<string>;
  titleWithLineBreaks: MaybeType<string>;
  useTitleWithLineBreaks: MaybeType<boolean>;
  darkMode: MaybeType<boolean>;
  videoBehavior?: MaybeType<VideoType>;
  videoControls?: MaybeType<boolean>;
}

interface CardProps {
  body: string;
  eyebrow: string;
  slug: string;
}

const Card = (props: CardProps) => {
  const {body, eyebrow, slug} = props;

  return (
    <div className={css(styles.searchHero__card)}>
      <p className={css("eyebrow", styles.searchHero__card__eyebrow)}>
        {eyebrow}
      </p>

      <h4 className={css("h4", styles.searchHero__card__body)}>{body}</h4>

      <LinkTag
        href={slug}
        classes={css(styles.searchHero__card__link)}
        openInNewTab
      >
        <span className={css("h5")}>{translate("Read")}</span>
      </LinkTag>
    </div>
  );
};

const GATSBY_ALGOLIA_APP_ID = process.env.GATSBY_ALGOLIA_APP_ID || "";
const GATSBY_ALGOLIA_READ_KEY = process.env.GATSBY_ALGOLIA_READ_KEY || "";
const ITEMS_PER_PAGE: number = 8;

const SEARCH_CLIENT = algoliasearch(
  GATSBY_ALGOLIA_APP_ID,
  GATSBY_ALGOLIA_READ_KEY
);

const SearchHero = (props: SearchHeroProps): ReactElement | null => {
  const {
    algoliaIndex,
    bgImage,
    bgVideo,
    wistiaBgVideo,
    copy,
    ctas,
    eyebrow,
    darkMode,
    eyebrowIcon,
    headline,
    titleWithLineBreaks,
    useTitleWithLineBreaks,
    videoBehavior,
    videoControls,
  } = props;

  const [currentPage, setCurrentPage] = React.useState(0);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [inputValue, setInputValue] = React.useState("");
  const [results, setResults] = React.useState<Array<any>>([]);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const algoliaIndexClient = React.useRef<SearchIndex | null>(null);

  React.useEffect(() => {
    if (!algoliaIndex) {
      return;
    }

    algoliaIndexClient.current = SEARCH_CLIENT.initIndex(algoliaIndex);
  }, [algoliaIndex]);

  const search = React.useCallback(async () => {
    if (!algoliaIndexClient.current) {
      return;
    }

    const query = inputValue.trim();

    if (query.length) {
      const res = await algoliaIndexClient.current.search(query, {
        hitsPerPage: 1000,
        alternativesAsExact: ["singleWordSynonym"],
      });

      const {hits} = res;

      setCurrentPage(1);
      setSearchQuery(query);
      setResults(hits);
    }
  }, [inputValue]);

  const inputKeyUp = (e: KeyboardEvent) => {
    if (e.key === KEY.ENTER) {
      search();
    }
  };

  const inputOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.currentTarget.value);
  };

  const getResultsRange = (): string => {
    // example: Showing 0 results for foobar
    if (results.length === 0) {
      return "0";
    }

    // example: Showing 5 results for foobar
    if (results.length <= ITEMS_PER_PAGE) {
      return `${results.length}`;
    }

    // example: Showing 1 - 8 out of 100 results for foobar
    const startIndex = ITEMS_PER_PAGE * (currentPage - 1) + 1;
    const endIndex =
      results.length < ITEMS_PER_PAGE
        ? results.length
        : Math.min(ITEMS_PER_PAGE * currentPage, results.length);

    return `${startIndex} - ${endIndex} ${translate("out of")} ${
      results.length
    }`;
  };

  const constructVideoComponent = () => {
    if (bgVideo == null && wistiaBgVideo == null) {
      return null;
    }

    if (wistiaBgVideo != null) {
      return (
        <div className={css(styles.searchHero__right__videoWrapper)}>
          <Video
            videoConfig={{
              wistia: {
                options: {
                  fitStrategy: "cover",
                },
              },
            }}
            videoTitle={headline!}
            type={VideoType.autoplay}
            videoSource={wistiaBgVideo}
            showControl={videoControls === true}
          />
        </div>
      );
    }

    return (
      <div className={css(styles.searchHero__right__videoWrapper)}>
        <Video
          videoTitle={headline!}
          type={videoBehavior!}
          videoSource={bgVideo!}
          videoClasses={styles.searchHero__right__video}
          showControl={videoControls === true}
        />
      </div>
    );
  };

  if (!algoliaIndex) {
    return null;
  }

  return (
    <div
      className={css(
        styles.searchHero,
        darkMode === true ? styles.searchHeroDark : ""
      )}
    >
      <ModuleBody>
        <div className={styles.searchHero__hero}>
          <div className={css(styles.searchHero__left)}>
            {eyebrow && (
              <div className={styles.searchHero__left__eyebrowWrapper}>
                {eyebrowIcon && (
                  <Image
                    image={eyebrowIcon}
                    classes={styles.searchHero__left__eyebrowIcon}
                  />
                )}
                <p className={css("eyebrow", styles.searchHero__left__eyebrow)}>
                  {eyebrow}
                </p>
              </div>
            )}

            {useTitleWithLineBreaks && titleWithLineBreaks != null && (
              <h2 className={css("h2", styles.searchHero__left__headline)}>
                <span
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html:
                      titleWithLineBreaks != null
                        ? titleWithLineBreaks.replace(/(\n|\r)/g, "<br/>")
                        : "",
                  }}
                />
              </h2>
            )}

            {!useTitleWithLineBreaks && headline != null && (
              <h2 className={css("h2", styles.searchHero__left__headline)}>
                {headline}
              </h2>
            )}

            <div className={css(styles.searchHero__left__search)}>
              <input
                className={css(styles.searchHero__left__searchInput)}
                placeholder={translate("Search by term or phrase...")}
                onKeyUp={inputKeyUp}
                value={inputValue}
                onChange={inputOnChange}
                ref={inputRef}
                aria-label={translate("Search")}
              />

              <Button
                kind="primary"
                classes={styles.searchHero__left__searchBtn}
                onClick={search}
              >
                {translate("Search")}
              </Button>
            </div>

            {copy && (
              <p className={css("p", styles.searchHero__left__copy)}>{copy}</p>
            )}

            {/* we will also assume that the first CTA would be primary, subsequent cta should be secondary */}
            {ctas && (
              <ul className={styles.searchHero__left__ctas}>
                {ctas.map((cta, i) => {
                  return (
                    <li
                      key={generateKeyId(`${cta.label.toLowerCase()}-${i}`)}
                      className={styles.searchHero__left__cta}
                    >
                      <CTAButton
                        darkMode={darkMode === true}
                        cta={cta}
                        kind={i === 0 ? "primary" : "secondary"}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className={css(styles.searchHero__right)}>
            {!bgVideo && !wistiaBgVideo && bgImage && (
              <Image
                image={bgImage}
                objectFit="cover"
                classes={styles.searchHero__right__img}
              />
            )}

            {constructVideoComponent()}
          </div>
        </div>

        {searchQuery.length > 0 && (
          <div className={css(styles.searchHero__results)}>
            <p className={css("p", styles.searchHero__resultsFeedback)}>
              {translate("Showing")} {getResultsRange()} {translate("result")}
              {results.length === 1 ? "" : "s"} {translate("for")}{" "}
              <span>{searchQuery}</span>
            </p>

            <ul
              className={css(
                styles.searchHero__results__list,
                results.length === 0
                  ? styles.searchHero__results__listEmpty
                  : ""
              )}
            >
              {results.length > 0 &&
                results
                  .slice(
                    (currentPage - 1) * ITEMS_PER_PAGE,
                    (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
                  )
                  .map((result: any, i: number) => {
                    return (
                      <li
                        key={generateKeyId(`${result.pageTitle}-${i}`)}
                        className={styles.searchHero__results__item}
                      >
                        <Card
                          eyebrow={result.contentType}
                          body={result.pageTitle}
                          slug={result.slug}
                        />
                      </li>
                    );
                  })}
            </ul>

            {results.length === 0 && (
              <h3 className={css("h3", styles.searchHero__results__emptyMsg)}>
                {translate("No results to display")}
              </h3>
            )}

            {results.length > ITEMS_PER_PAGE && (
              <SearchPagination
                isFirstPage={currentPage === 1}
                currentPageNumber={currentPage}
                isLastPage={
                  currentPage === Math.ceil(results.length / ITEMS_PER_PAGE)
                }
                darkMode={darkMode === true}
                pageCount={Math.ceil(results.length / ITEMS_PER_PAGE)}
                handlePage={(index: number) => setCurrentPage(index)}
              />
            )}
          </div>
        )}
      </ModuleBody>
    </div>
  );
};

export default SearchHero;
